import { createContext, type ReactNode, useContext, useEffect, useMemo, useReducer } from 'react';
import { AppContext } from './context';
import { trpc } from '@/config/trpc';

import { type components } from '~/common/types/kpc.d';

type InvoicingAffiliationResponse =
  components['schemas']['KeypointConnect.Finance.API.KeypointInvoicing.DTOs.Responses.Abstract.Accounts.IInvoicingAffiliationResponse'];

interface AffiliationsState {
  affiliations: InvoicingAffiliationResponse[];
  error: string | null;
  loading: boolean;
}

type AffiliationsAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: InvoicingAffiliationResponse[] }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'CLEAR' };

const initialState: AffiliationsState = {
  affiliations: [],
  error: null,
  loading: false,
};

function affiliationsReducer(
  state: AffiliationsState,
  action: AffiliationsAction,
): AffiliationsState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, error: null, loading: true };
    case 'FETCH_SUCCESS':
      return { affiliations: action.payload, error: null, loading: false };
    case 'FETCH_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR':
      return initialState;
    default:
      return state;
  }
}

interface AffiliationsContextType extends AffiliationsState {
  ready: boolean;
  retry: () => void;
  autocomplete: { data: InvoicingAffiliationResponse; label: string; value: string }[];
}

export const AffiliationsContext = createContext<AffiliationsContextType>({
  autocomplete: [],
  ...initialState,
  ready: false,
  retry: () => {},
});

export const AffiliationsProvider = ({ children }: { children: ReactNode }) => {
  const { currentTeamId } = useContext(AppContext);
  const [state, dispatch] = useReducer(affiliationsReducer, initialState);

  const {
    data: affiliations,
    isLoading,
    isError,
    error,
    refetch,
  } = trpc.finance.keypointInvoicing.affiliations.list.useQuery(undefined, {
    enabled: !!currentTeamId,
    retry: 1,
    trpc: { context: { skipBatch: true } },
  });

  useEffect(() => {
    if (!currentTeamId) {
      dispatch({ type: 'CLEAR' });
      return;
    }
    dispatch({ type: 'FETCH_START' });
    // If not loading and not error, set affiliations
    if (!isLoading && affiliations) {
      dispatch({ payload: affiliations, type: 'FETCH_SUCCESS' });
    } else if (isError && error) {
      dispatch({
        payload: (error as unknown as Error).message || 'Failed to load affiliations',
        type: 'FETCH_ERROR',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeamId, affiliations, isLoading, isError, error]);

  const ready = !state.loading && !state.error;

  const autocomplete = useMemo(
    () =>
      state.affiliations.map((affiliation) => ({
        data: affiliation,
        label: affiliation.accountVatNumber,
        value: affiliation.accountVatNumber,
      })),
    [state.affiliations],
  );

  const contextValue = useMemo(
    () => ({ ...state, autocomplete, ready, retry: () => refetch() }),
    [state, ready, refetch, autocomplete],
  );

  return (
    <AffiliationsContext.Provider value={contextValue}>{children}</AffiliationsContext.Provider>
  );
};

export const useAffiliations = () => useContext(AffiliationsContext);
